import { defineStore } from 'pinia'
import http from '@/http-common'
import { authStore } from './auth'

// Some backends return permission objects without `codename`.
// In those cases, certain app-level gates (like `can_give_permissions`) must be mapped
// to the human-readable permission name returned by the API.
// Note: keys are pre-normalized (lowercase, alphanumeric only) to avoid calling helpers before declaration.
const PERMISSION_ALIASES: Record<string, string[]> = {
  // normalizathor('can_give_permissions') === 'cangivepermissions'
  cangivepermissions: ['Pode conceder permissões', 'Pode conceder permissoes', 'Can give permissions'],
  viewusuario: ['view_user', 'Can view user', 'Can view usuário', 'Pode visualizar usuário'],
  addusuario: ['add_user', 'Can add user', 'Can add usuário', 'Pode adicionar usuário'],
  changeusuario: ['change_user', 'Can change user', 'Can change usuário', 'Pode mudar usuário'],
  deleteusuario: ['delete_user', 'Can delete user', 'Can delete usuário', 'Pode apagar usuário'],
  viewcompanyuser: ['view_companyuser', 'Can view company user', 'Pode visualizar company user'],
  addcompanyuser: ['add_companyuser', 'Can add company user', 'Pode adicionar company user'],
  changecompanyuser: ['change_companyuser', 'Can change company user', 'Pode mudar company user'],
  deletecompanyuser: ['delete_companyuser', 'Can delete company user', 'Pode apagar company user'],
  viewcompanygroup: ['view_companygroup', 'Can view company group', 'Pode visualizar company group'],
  addcompanygroup: ['add_companygroup', 'Can add company group', 'Pode adicionar company group'],
  changecompanygroup: ['change_companygroup', 'Can change company group', 'Pode mudar company group'],
  deletecompanygroup: ['delete_companygroup', 'Can delete company group', 'Pode apagar company group'],
  viewempresa: ['view_empresa', 'acessa_api_empresa', 'Can view empresa', 'Pode visualizar empresa'],
  addempresa: ['add_empresa', 'Can add empresa', 'Pode adicionar empresa'],
  changeempresa: ['change_empresa', 'Can change empresa', 'Pode mudar empresa'],
  deleteempresa: ['delete_empresa', 'Can delete empresa', 'Pode apagar empresa'],
  viewcompany: ['view_company', 'Can view company', 'Pode visualizar company'],
  changecompany: ['change_company', 'Can change company', 'Pode mudar company'],
  viewconfigempresa: [
    'view_configempresa',
    'Can view configuração empresa',
    'Can view configuracao empresa',
    'Pode visualizar configuração empresa',
    'Pode visualizar configuracao empresa',
  ],
  changeconfigempresa: [
    'change_configempresa',
    'Can change configuração empresa',
    'Can change configuracao empresa',
    'Pode mudar configuração empresa',
    'Pode mudar configuracao empresa',
  ],
  viewbusinesshour: ['view_businesshour', 'Can view business hour', 'Pode visualizar business hour'],
  changebusinesshour: ['change_businesshour', 'Can change business hour', 'Pode mudar business hour'],
  viewbotconfiguration: ['view_bot_configuration', 'Pode ver a configuração do bot', 'Pode ver a configuracao do bot'],
  changebotconfiguration: [
    'change_bot_configuration',
    'Pode alterar a configuração comercial do bot',
    'Pode alterar a configuracao comercial do bot',
  ],
  viewbotoperations: [
    'view_bot_operations',
    'Pode ver conversas e decisões do bot',
    'Pode ver conversas e decisoes do bot',
  ],
  viewbottechnicaldiagnostics: [
    'view_bot_technical_diagnostics',
    'Pode ver diagnósticos técnicos do bot',
    'Pode ver diagnosticos tecnicos do bot',
  ],
  changebotadvancedrules: [
    'change_bot_advanced_rules',
    'Pode alterar regras avançadas do bot',
    'Pode alterar regras avancadas do bot',
  ],
}

export const permissionStore = defineStore('permissions', {
  state: () => ({
    // Start as null so we can reliably hydrate from localStorage on refresh
    permissions: null as any[] | null,
  }),

  actions: {
    async loadMyPermissions() {
      try {
        const resp = await http.get('/usuariolist/mycompanypermissions/')

        localStorage.setItem('permissions', JSON.stringify(resp.data))

        this.permissions = resp.data
      } catch (error) {
        console.error('Erro ao buscar permissões', error)
        this.permissions = null
      }
    },

    loadPermissionsFromStorage() {
      const storedPermissions = localStorage.getItem('permissions')
      if (storedPermissions) {
        this.permissions = JSON.parse(storedPermissions)
      } else {
        this.permissions = null
      }
    },
  },

  getters: {
    hasPermission:
      (state) =>
      (permissionName: string): boolean => {
        const normalizedPermissionName = normalizathor(permissionName)
        const normalizedAliases = PERMISSION_ALIASES[normalizedPermissionName] || []
        const acceptedPermissionNames = new Set(
          [normalizedPermissionName, ...normalizedAliases].map((p) => normalizathor(p)),
        )
        const auth = authStore()
        const isTenantAdmin =
          !!auth.token &&
          !!auth.cmpId &&
          (String(auth.cmpUrole).toLowerCase() === 'admin' || (!auth.cmpUrole && String(auth.cmpUadmin) === 'true'))

        // Internal/role-based permission gates (not backed by /mypermissions)
        if (normalizedPermissionName === normalizathor('reports:access')) {
          const isMember = !!auth.token && !!auth.cmpId
          if (!isMember) return false
          if (isTenantAdmin) return true
          const role = (auth.cmpUrole || '').toLowerCase()
          return (
            role === 'admin' ||
            role === 'manager' ||
            role === 'system' ||
            [
              'view_reporttemplate',
              'add_reporttemplate',
              'change_reporttemplate',
              'delete_reporttemplate',
              'use_general_reports',
              'use_client_reports',
              'use_vehicle_reports',
              'use_sale_reports',
              'use_proposal_reports',
            ].some((codename) => hasStoredPermission(state, codename))
          )
        }

        if (normalizedPermissionName === normalizathor('reports:manage')) {
          const isMember = !!auth.token && !!auth.cmpId
          if (!isMember) return false

          if (isTenantAdmin) return true

          const role = (auth.cmpUrole || '').toLowerCase()
          if (role === 'admin' || role === 'manager') return true

          return ['add_reporttemplate', 'change_reporttemplate', 'delete_reporttemplate'].some((codename) =>
            hasStoredPermission(state, codename),
          )
        }

        if (isTenantAdmin) return true

        // Ensure we can still work after page reloads where Pinia state is empty
        // (router guard may run before loadMyPermissions)
        if (!state.permissions || state.permissions.length === 0) {
          const stored = localStorage.getItem('permissions')
          if (stored) {
            try {
              const parsed = JSON.parse(stored)
              state.permissions = Array.isArray(parsed) ? parsed : null
            } catch (_) {
              state.permissions = null
            }
          }
        }

        if (!state.permissions || state.permissions.length === 0) return false

        return state.permissions.some((permission) => {
          const candidates: string[] = []

          if (typeof permission === 'string') {
            candidates.push(permission)
          } else if (permission && typeof permission === 'object') {
            // Backend may return objects like { codename }, { name }, or full Permission objects
            const p: any = permission
            if (p.codename) candidates.push(String(p.codename))
            if (p.name) candidates.push(String(p.name))
            if (p.permission) candidates.push(String(p.permission))
            if (p.translated_name) candidates.push(String(p.translated_name))
            if (p.label) candidates.push(String(p.label))
          }

          return candidates.some((c) => {
            const normalized = normalizathor(c)
            // Prefer exact match for codenames, keep includes for legacy strings
            for (const target of acceptedPermissionNames) {
              if (normalized === target || normalized.includes(target)) return true
            }
            return false
          })
        })
      },
  },
})

const normalizathor = (param: string): string => {
  return (
    param
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      // Make comparisons resilient across formats:
      // - "can_give_permissions" vs "can give permissions"
      // - "reports:access" vs "reports access"
      .replace(/[^a-z0-9]/g, '')
  )
}

const hasStoredPermission = (state: { permissions: any[] | null }, permissionName: string): boolean => {
  if (!state.permissions || state.permissions.length === 0) {
    const stored = localStorage.getItem('permissions')
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        state.permissions = Array.isArray(parsed) ? parsed : null
      } catch (_) {
        state.permissions = null
      }
    }
  }

  const target = normalizathor(permissionName)
  return Boolean(
    state.permissions?.some((permission: any) => {
      const candidates =
        typeof permission === 'string'
          ? [permission]
          : [
              permission?.codename,
              permission?.name,
              permission?.permission,
              permission?.translated_name,
              permission?.label,
            ]
      return candidates.some((candidate) => candidate && normalizathor(String(candidate)) === target)
    }),
  )
}
